/**
 * Single source of truth for event-card sizing.
 *
 * Each slider level maps to a responsive column range: cards grow/shrink with
 * the viewport between `min` and `max`, wrapping to a new row when another
 * `min`-wide column fits. The slider (toolbar) and the grids both read from here.
 */

export type CardSizeLevel = 1 | 2 | 3 | 4;

export const DEFAULT_CARD_SIZE: CardSizeLevel = 3;

export const CARD_SIZE_LEVELS: Record<CardSizeLevel, { min: number; max: number }> = {
  1: { min: 240, max: 320 },
  2: { min: 290, max: 370 },
  3: { min: 340, max: 420 },
  4: { min: 390, max: 470 }
};

/*
 * CSS `grid-template-columns` for a given level.
 *
 * Uses `minmax(min, 1fr)` (not a fixed max) so cards genuinely grow/shrink with
 * the viewport: with a definite max, browsers compute the column count from the
 * max and the cards stay pinned at that size. The max is enforced separately via
 * `cardMaxWidth` on each card.
 */
export const cardColumns = (level: CardSizeLevel): string =>
  `repeat(auto-fill, minmax(${CARD_SIZE_LEVELS[level].min}px, 1fr))`;

// Max width cap applied to each card so columns don't grow past the level's max.
export const cardMaxWidth = (level: CardSizeLevel): string => `${CARD_SIZE_LEVELS[level].max}px`;

// Card title font-size, scaling with the level: 13px (level 1) → 16px (level 4).
export const cardTitleFontSize = (level: CardSizeLevel): string => `${12 + level}px`;

// Header action icon size: 11px (level 1), 12px (level 2), 13px (levels 3–4).
export const cardActionFontSize = (level: CardSizeLevel): string => `${Math.min(13, 10 + level)}px`;

// Clamps any number to a valid CardSizeLevel (e.g. from a slider input).
export const toCardSizeLevel = (n: number): CardSizeLevel =>
  n <= 1 ? 1 : n >= 4 ? 4 : n === 2 ? 2 : 3;
